import * as gsap from "gsap";
import * as HLF from "html-living-framework";
import {Application} from "../Application";
import {Anchor} from "../core/Anchor";
import {texture} from "../core/AssetHelpers";
import {Container} from "../core/Container";
import {ContainerExtensions} from "../core/ContainerExtensions";
import {IPoint, IRectangle} from "../core/Geom";
import {Sprite} from "../core/Sprite";
import {IFocusableRect} from "./Focusable";

export class KeyboardFocusLayer extends Container {
    public static get instance(): KeyboardFocusLayer {
        return this._instance || (this._instance = new KeyboardFocusLayer());
    }

    get currentFocusable(): HLF.Input.IFocusable | undefined {
        return this._currentFocusable;
    }

    set currentFocusable(value: HLF.Input.IFocusable | undefined) {
        this._currentFocusable = value;
    }

    private static _instance: KeyboardFocusLayer;

    private readonly rect = this.addChild(new KeyboardFocusRect());
    private _currentFocusable?: HLF.Input.IFocusable;

    constructor() {
        super();

        this.rect.visible = false;
        Application.instance.ticker.add(this.update, this, 100);
        Application.instance.stage.addChild(this);
    }

    public update(time: number) {
        let isVisible = false;
        const control = this._currentFocusable;
        if (control && (control as any).parent) {
            const rect = control instanceof PIXI.DisplayObject ? this.getKeyboardControlRect(control) : undefined;
            if (rect) {
                isVisible = true;
                this.rect.show();
                this.rect.setRect(rect);
            }
        }
        if (!isVisible) {
            this.rect.hide();
        }
    }

    public destroy(): void {
        Application.instance.ticker.remove(this.update, this);
        super.destroy();
    }

    private getKeyboardControlRect(target: PIXI.DisplayObject): IRectangle {
        if ((target as any as IFocusableRect).getFocusRect) {
            return (target as any as IFocusableRect).getFocusRect!();
        }
        return ContainerExtensions.getGlobalBounds.call(target, this);
    }
}

class KeyboardFocusRect extends Container {

    private readonly corners = [
        Anchor.topLeft,
        Anchor.topRight,
        Anchor.bottomLeft,
        Anchor.bottomRight,
    ].map((p) => this.addChild(new KeyboardFocusCorner(p)));
    private _isShown?: boolean;

    public show() {
        if (!this._isShown) {
            this._isShown = true;
            this.visible = true;
            for (const corner of this.corners) {
                corner.startTween();
            }
        }
    }

    public hide() {
        if (this._isShown) {
            this._isShown = false;
            for (const corner of this.corners) {
                corner.stopTween();
            }
            this.visible = false;
        }
    }

    public setRect(rect: IRectangle) {
        this.x = rect.x;
        this.y = rect.y;

        this.corners[0].x = 0;
        this.corners[0].y = 0;
        this.corners[1].x = rect.width;
        this.corners[1].y = 0;
        this.corners[2].x = 0;
        this.corners[2].y = rect.height;
        this.corners[3].x = rect.width;
        this.corners[3].y = rect.height;
    }
}

class KeyboardFocusCorner extends Sprite {

    private readonly _anchor0 = {x: 0, y: 0};
    private readonly _anchor1 = {x: 0, y: 0};
    private _param = 0;

    constructor(p: IPoint) {
        super(texture(`keyboard-focus-${p.y}${p.x}.png`), {
            x: 0.6 - p.x * 0.2,
            y: 0.6 - p.y * 0.2,
        });
        this._anchor0.x = this.anchor.x;
        this._anchor0.y = this.anchor.y;
        this._anchor1.x = 0.3 + p.x * 0.4;
        this._anchor1.y = 0.3 + p.y * 0.4;
    }

    get param(): number {
        return this._param;
    }

    set param(value: number) {
        this._param = value;
        this.anchor.x = this._anchor0.x + value * (this._anchor1.x - this._anchor0.x);
        this.anchor.y = this._anchor0.y + value * (this._anchor1.y - this._anchor0.y);
    }

    public startTween() {
        this.stopTween();
        gsap.TweenMax.to(this, 0.3, {
            ease: gsap.Sine.easeIn,
            delay: 0.2,
            param: 1,
            onComplete: () => {
                gsap.TweenMax.to(this, 0.3, {
                    ease: gsap.Sine.easeOut,
                    param: 0,
                    onComplete: () => {
                        this.startTween();
                    },
                });
            },
        });
    }

    public stopTween() {
        this.killTweens();
        this.anchor.x = this._anchor0.x;
        this.anchor.y = this._anchor0.y;
    }
}
